// A tool opened from a menu on a tablet (issue 1179): its panel slides over the board — in from the right on a
// landscape screen, up from the bottom on a portrait one (`sheetPlacement`) — so every tool is one tap from the top
// bar without rearranging the bars. "Put it on a bar…" opens the tool's chooser (promotion, as on a desktop); Close,
// Escape or a tap on the board's uncovered part puts it away. Nothing about the tool changes: it is the same panel a
// bar would hold, and the bars are still shortcuts (design/07 §2).
import { useEffect, useRef, useState } from 'react';
import { sheetPlacement } from './barFit';
import { ToolBody, type ToolDef } from './tools';

function useScreen(): { width: number; height: number } {
  const read = () => ({ width: window.innerWidth, height: window.innerHeight });
  const [size, setSize] = useState(read);
  useEffect(() => {
    const on = () => setSize(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return size;
}

export function ToolSheet({ tool, onPlace, onClose }: { tool: ToolDef; onPlace: () => void; onClose: () => void }) {
  const { width, height } = useScreen();
  const place = sheetPlacement(width, height);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => before?.focus();
  }, []);
  const box = place.edge === 'right'
    ? { top: 0, right: 0, bottom: 0, width: place.px }
    : { left: 0, right: 0, bottom: 0, height: place.px };
  return (
    <div className="fixed inset-0 z-40" data-testid="tool-sheet-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section ref={ref} role="dialog" aria-label={tool.title} tabIndex={-1} data-testid="tool-sheet" data-edge={place.edge}
        className={`absolute flex flex-col border-vtt-gold-dim bg-vtt-card text-stone-100 shadow-2xl outline-none ${place.edge === 'right' ? 'border-l' : 'border-t rounded-t-lg'}`}
        style={box} onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
        <header className="flex items-center gap-2 border-b border-stone-600 bg-gradient-to-b from-[rgb(var(--vtt-bar-head-top))] to-[rgb(var(--vtt-bar-head-bottom))] px-2 py-1">
          <h2 className="vtt-title-caps min-w-0 flex-1 truncate text-[0.8125rem]">{tool.title}</h2>
          <button type="button" className="vtt-btn rounded border border-stone-600 px-3 py-1 text-sm hover:bg-stone-800" onClick={onPlace}
            aria-label={`Put ${tool.title} on a bar…`}>
            On a bar…
          </button>
          <button type="button" className="rounded px-3 py-1 text-lg leading-none hover:bg-stone-800" onClick={onClose} aria-label={`Close ${tool.title}`}>
            ×
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto" data-testid={`sheet-${tool.id}`}>
          <ToolBody tool={tool.id} />
        </div>
      </section>
    </div>
  );
}
